import { useEffect, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import { S3_CDN_DOMAIN } from "../../constants";
import Modal from "../modal-new";
import SvgIcon from "../SvgIcon";
import { getExerciseName } from "./learn-more/blockHelpers";
import { exerciseVideoListCommentActions } from "./thread-comments/comment-action-configs";
import { CommentsSection } from "./thread-comments/comments-section";

const JWPlayerVideo = ({
  locals,
  originalId,
  athleteId,
  exerciseToEdit,
  reviewedByTheCoach,
  setIndex,
  videoIndex,
  threadId,
  utcDate,
  primary,
  secondary,
  type,
  movement,
  prevHref,
  nextHref,
  setNumber,
  setTarget,
  setComment,
  t,
}) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const commentingDisabled =
    !threadId && !exerciseToEdit && !setIndex && !videoIndex;

  const bodyRef = useRef(null);

  useEffect(() => {
    // if we have to check if this exercise contains a video that possibly need to get reviewed by coach
    if (exerciseToEdit) {
      let modifiedBody = {};
      let isReviewed = true;

      const alreadyReviewed =
        reviewedByTheCoach === true || reviewedByTheCoach === "true";
      const parsedSetIndex = parseInt(setIndex, 10);
      const parsedVideoIndex = parseInt(videoIndex, 10);
      const hasValidIndices =
        !Number.isNaN(parsedSetIndex) && !Number.isNaN(parsedVideoIndex);

      if (alreadyReviewed) {
        isReviewed = true;
      } else if (hasValidIndices) {
        modifiedBody = {
          setIndex: String(parsedSetIndex),
          videoIndex: String(parsedVideoIndex),
          exerciseId: exerciseToEdit,
        };
        isReviewed = false;
      }

      //if is already reviewed or not
      if (!isReviewed) {
        // Plain fetch, not the SPA's form-submit/navigation helper: this is a
        // silent background confirmation, and routing it through the app's
        // client-side navigation layer let a slow-resolving response redirect
        // the coach to whatever page/video the shared history state pointed at
        // by the time it finished - not necessarily the one being reviewed here,
        // which could bounce the modal between videos (sc-44871).
        fetch(`/actions/exercise/edit-set-video`, {
          method: "POST",
          credentials: "same-origin",
          body: new URLSearchParams(modifiedBody),
        }).catch(() => {});
      }
    }
  }, [exerciseToEdit, reviewedByTheCoach, setIndex, videoIndex]);

  const handlePlayPause = () => {
    const video = videoRef.current;
    if (video) {
      if (isPlaying) {
        video.pause();
      } else {
        video.play();
      }
    }
    setIsPlaying((prev) => !prev);
  };

  const videoOriginal = originalId?.replace(/\.[^/.]+$/, ".mp4");
  const url = `${S3_CDN_DOMAIN}exercise_set_videos/${athleteId}/${videoOriginal}`;

  const exerciseName =
    primary || secondary ? getExerciseName({ primary, secondary, type }) : null;
  const displaySetNumber = setNumber != null ? setNumber : Number(setIndex) + 1;

  return (
    <Modal
      className="one-video-modal"
      title={t("jwplayerVideo.title")}
      toClear={[
        "threadId",
        "exerciseToEdit",
        "setIndex",
        "videoIndex",
        "originalId",
        "reviewedByTheCoach",
        "uploaderId",
        "utcDate",
        "setNumber",
        "setTarget",
        "setComment",
      ]}
    >
      <div className="body" ref={bodyRef}>
        {exerciseName && (
          <header className="pt-8 px-8">
            <p className="s1 mb-1">{exerciseName}</p>
            <p className="p4">
              {setTarget && <span>{setTarget}</span>}
              {setTarget && <span> | </span>}
              <span>(Set #{displaySetNumber})</span>
            </p>
            {setComment && (
              <p className="p4 mt-1">
                {t("jwplayerVideo.setComment")}: {setComment}
              </p>
            )}
          </header>
        )}
        <div className="exercise-library-video-iframe flex-col">
          {!isPlaying && (
            <SvgIcon
              src="/img/new-icons/play-white.svg"
              classNames="play-icon"
              onClickFn={handlePlayPause}
              noFilterOnLight={true}
            />
          )}
          <video
            ref={videoRef}
            controls={isPlaying}
            controlsList="nodownload"
            disablePictureInPicture
            src={url}
          />
          <div className="flex justify-between mt-3 px-4">
            <a
              href={prevHref}
              role="button"
              data-button-type="tertiary"
              data-icon
              className={`${!prevHref ? "invisible pointer-events-none" : "ml-8"}`}
              aria-disabled={!prevHref}
            >
              <SvgIcon
                src="/img/new-icons/arrow-forward.svg"
                classNames="-rotate-180 w-4"
              />
              {t("jwplayerVideo.previous")}
            </a>
            <a
              href={url}
              target="_blank"
              download="download"
              role="button"
              data-button-type="secondary"
              data-icon
            >
              <SvgIcon
                src="/img/new-icons/arrow-forward.svg"
                classNames="rotate-90 w-4"
              />
              {t("jwplayerVideo.download")}
            </a>
            <a
              href={nextHref}
              role="button"
              data-button-type="tertiary"
              data-icon
              className={`${!nextHref ? "invisible pointer-events-none" : "mr-8"}`}
              aria-disabled={!nextHref}
            >
              {t("jwplayerVideo.next")}
              <SvgIcon
                src="/img/new-icons/arrow-forward.svg"
                classNames="w-4"
              />
            </a>
          </div>
        </div>
        <CommentsSection
          bodyRef={bodyRef}
          commentActions={exerciseVideoListCommentActions}
          commentParams={{
            exerciseId: exerciseToEdit,
            setIndex,
            videoIndex,
            threadId,
            receiverId: athleteId,
            utcDate,
            primary,
            secondary,
            type,
            movement,
          }}
          locals={locals}
          commentingDisabled={commentingDisabled}
        />
      </div>
    </Modal>
  );
};

export default withTranslation("coaching-portal")(JWPlayerVideo);
